import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Search, Home, Building2, Factory, Landmark, Layers } from "lucide-react";

// Categories including the newly added Government type
const CATEGORY_META = {
    residential: { label: "Residential", icon: Home, tone: "bg-blue-50 text-blue-700 border-blue-200" },
    commercial: { label: "Commercial", icon: Building2, tone: "bg-amber-50 text-amber-700 border-amber-200" },
    industrial: { label: "Industrial", icon: Factory, tone: "bg-zinc-100 text-zinc-700 border-zinc-300" },
    government: { label: "Govt", icon: Landmark, tone: "bg-green-50 text-green-700 border-green-200" },
    uncategorized: { label: "Uncategorized", icon: Layers, tone: "bg-zinc-50 text-zinc-500 border-zinc-200" },
};

const GROUP_ORDER = ["residential", "commercial", "industrial", "government", "uncategorized"];

export default function Plants() {
    const [plants, setPlants] = useState([]);
    const [q, setQ] = useState("");
    const [activeGroup, setActiveGroup] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => { api.get("/plants").then(r => setPlants(r.data)).finally(() => setLoading(false)); }, []);

    const filtered = plants.filter(p =>
        !q || p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.address?.toLowerCase().includes(q.toLowerCase()) ||
        p.owner_name?.toLowerCase().includes(q.toLowerCase())
    );

    // Group by category (Residential / Commercial / Industrial / Govt / Uncategorized)
    const grouped = useMemo(() => {
        const g = {};
        for (const p of filtered) {
            const key = CATEGORY_META[p.category] ? p.category : "uncategorized";
            g[key] = g[key] || [];
            g[key].push(p);
        }
        return g;
    }, [filtered]);

    const counts = {};
    for (const p of plants) {
        const key = CATEGORY_META[p.category] ? p.category : "uncategorized";
        counts[key] = (counts[key] || 0) + 1;
    }

    const visibleGroups = activeGroup === "all"
        ? GROUP_ORDER.filter(k => (grouped[k] || []).length > 0)
        : [activeGroup].filter(k => (grouped[k] || []).length > 0);

    return (
        <div className="space-y-6" data-testid="plants-page">
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Installations</div>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Fleet ({plants.length})</h1>
                <p className="text-sm text-zinc-500 mt-1">Grouped by plant type · set the category on any Plant Detail page.</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, location, customer…" className="pl-10 h-10 rounded-full bg-zinc-100 border-transparent focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="plants-search" />
                </div>
                <div className="inline-flex gap-1.5 flex-wrap" data-testid="plants-group-tabs">
                    <FilterTab active={activeGroup === "all"} onClick={() => setActiveGroup("all")} testid="group-tab-all">
                        <Layers className="w-3.5 h-3.5 mr-1" /> All <span className="ml-1.5 font-mono text-[10px] opacity-70">{plants.length}</span>
                    </FilterTab>
                    {GROUP_ORDER.map(key => {
                        const meta = CATEGORY_META[key];
                        const Icon = meta.icon;
                        const count = counts[key] || 0;
                        if (count === 0) return null;
                        return (
                            <FilterTab
                                key={key}
                                active={activeGroup === key}
                                onClick={() => setActiveGroup(key)}
                                testid={`group-tab-${key}`}
                            >
                                <Icon className="w-3.5 h-3.5 mr-1" /> {meta.label} <span className="ml-1 font-mono text-[10px] opacity-70">{count}</span>
                            </FilterTab>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="bg-white border border-zinc-200 rounded-sm p-6 text-sm text-zinc-500">Loading…</div>
            ) : visibleGroups.length === 0 ? (
                <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center text-sm text-zinc-500">No plants match this view.</div>
            ) : (
                <div className="space-y-6">
                    {visibleGroups.map(key => (
                        <PlantGroupTable
                            key={key}
                            groupKey={key}
                            plants={grouped[key] || []}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterTab({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center px-4 h-9 text-[11px] uppercase tracking-[0.14em] font-semibold rounded-full transition-colors duration-200 ${active ? "bg-[#0A0A0A] text-white" : "bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50"}`}
        >
            {children}
        </button>
    );
}

function PlantGroupTable({ groupKey, plants }) {
    const meta = CATEGORY_META[groupKey];
    const Icon = meta.icon;
    return (
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid={`plant-group-${groupKey}`}>
            <div className="flex items-center justify-between p-5 border-b border-zinc-100 bg-zinc-50/60">
                <div className="flex items-center gap-2.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>
                        <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                    <span className="text-xs font-mono text-zinc-500">{plants.length} plant{plants.length === 1 ? "" : "s"}</span>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">
                    Σ {plants.reduce((a, p) => a + (p.installed_capacity_kw || 0), 0).toFixed(1)} kWp
                </span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid={`plants-table-${groupKey}`}>
                <thead>
                    <tr className="text-xs uppercase tracking-wider text-zinc-500 bg-white border-b border-zinc-200">
                        <th className="py-2.5 px-4 text-left font-semibold">Plant</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Location</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Capacity</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Live Power</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Health</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Alerts</th>
                        <th className="py-2.5 px-4 text-left font-semibold">Customer</th>
                    </tr>
                </thead>
                <tbody>
                    {plants.map((p, i) => (
                        <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`plant-list-row-${groupKey}-${i}`}>
                            <td className="py-3 px-4">
                                <Link to={`/plants/${p.id}`} className="text-zinc-900 hover:text-[#0A0A0A] font-medium">
                                    {p.customer_name || p.name}
                                </Link>
                                {p.customer_name && p.location && (
                                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{p.location} · {p.name}</div>
                                )}
                            </td>
                            <td className="py-3 px-4 text-zinc-600">{p.address}</td>
                            <td className="py-3 px-4 font-mono text-zinc-700">{p.installed_capacity_kw} kWp</td>
                            <td className="py-3 px-4 font-mono text-zinc-700">{p.current_power_kw?.toFixed(1)} kW</td>
                            <td className="py-3 px-4"><StatusBadge health={p.health} /></td>
                            <td className="py-3 px-4 font-mono text-zinc-700">{p.active_alerts}</td>
                            <td className="py-3 px-4 text-zinc-600">{p.owner_name}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
}
